import { FiEdit2, FiRefreshCcw, FiTrash2, FiUsers } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { CurrencyValue } from '../ui/CurrencyValue';
import { HiddenFinancialValue } from '../ui/HiddenFinancialValue';
import { StatusBadge } from '../ui/StatusBadge';
import { AppTooltip } from '../ui/AppTooltip';

const ActionButton = ({ label, danger, onClick, children }) => (
  <AppTooltip label={label}>
    <button type='button' aria-label={label} onClick={onClick} className={`flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] border transition ${danger ? 'border-red-500/25 bg-red-500/10 text-[var(--danger)] hover:bg-red-500/20' : 'border-[var(--border-subtle)] bg-[var(--surface-panel)] text-[var(--text-secondary)] hover:border-[var(--brand)] hover:text-[var(--brand)]'}`}>
      {children}
    </button>
  </AppTooltip>
);

const ActionLink = ({ label, to, children }) => <AppTooltip label={label}><Link to={to} aria-label={label} className='flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel)] text-[var(--text-secondary)] transition hover:border-[var(--brand)] hover:text-[var(--brand)]'>{children}</Link></AppTooltip>;

const FinancialValue = ({ children, showValue, t, className = '' }) => showValue
  ? children
  : <HiddenFinancialValue t={t} className={className} />;

const FinancialValues = ({ item, showValue, t }) => (
  <>
    <td className='px-4 py-4 text-right'><FinancialValue showValue={showValue} t={t} className='justify-end'><CurrencyValue value={item.totalPurchase} /></FinancialValue></td>
    <td className='px-4 py-4 text-right'><FinancialValue showValue={showValue} t={t} className='justify-end'><CurrencyValue value={-Number(item.totalPayment || 0)} signed /></FinancialValue></td>
    <td className='px-4 py-4 text-right'><FinancialValue showValue={showValue} t={t} className='justify-end'><CurrencyValue value={item.totalBalance} signed className='font-semibold' /></FinancialValue></td>
  </>
);

export const DirectoryTable = ({ kind, items, showValue, onEdit, onDelete, onReconcile, canReconcile = false, t }) => {
  const isCustomer = kind === 'customer';
  const name = (item) => isCustomer ? `${item.firstName} ${item.lastName}` : item.name;
  const link = (item) => isCustomer
    ? `/transactions?customer_id=${item._id}&supplier_id=`
    : `/transactions?customer_id=&supplier_id=${item._id}`;

  return (
    <>
      <div className='grid gap-3 md:hidden'>
        {items.map((item) => (
          <article key={item._id} className='rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel)] p-4 shadow-sm'>
            <div className='flex items-start justify-between gap-3'>
              <div className='min-w-0'>
                {item.role === 'admin' ? <p className='font-bold'>{name(item)}</p> : <Link to={link(item)} className='font-bold text-[var(--brand)]'>{name(item)}</Link>}
                <p className='mt-1 truncate text-xs text-[var(--text-muted)]'>{item.email}</p>
                {item.role === 'admin' && <StatusBadge tone='neutral'>{t('Admin')}</StatusBadge>}
              </div>
              <div className='flex gap-2'>
                {isCustomer && <ActionLink label={t('View associated users')} to={`/users?clientId=${item._id}`}><FiUsers /></ActionLink>}
                {isCustomer && canReconcile && item.role !== 'admin' && <ActionButton label={t('Reconcile')} onClick={() => onReconcile(item)}><FiRefreshCcw /></ActionButton>}
                <ActionButton label={t('Edit')} onClick={() => onEdit(item)}><FiEdit2 /></ActionButton>
                <ActionButton label={t('Delete')} danger onClick={() => onDelete(item)}><FiTrash2 /></ActionButton>
              </div>
            </div>
            {item.role !== 'admin' && (
              <dl className='mt-4 grid grid-cols-3 gap-2 border-t border-[var(--border-subtle)] pt-3 text-xs'>
                <div><dt className='text-[var(--text-muted)]'>{t('Purchases')}</dt><dd className='mt-1'><FinancialValue showValue={showValue} t={t}><CurrencyValue value={item.totalPurchase} /></FinancialValue></dd></div>
                <div><dt className='text-[var(--text-muted)]'>{t('Payments')}</dt><dd className='mt-1'><FinancialValue showValue={showValue} t={t}><CurrencyValue value={-Number(item.totalPayment || 0)} signed /></FinancialValue></dd></div>
                <div><dt className='text-[var(--text-muted)]'>{t('Balance')}</dt><dd className='mt-1'><FinancialValue showValue={showValue} t={t}><CurrencyValue value={item.totalBalance} signed /></FinancialValue></dd></div>
              </dl>
            )}
          </article>
        ))}
      </div>

      <div className='directory-table-frame app-scrollbar hidden overflow-x-auto md:block'>
        <table className='directory-table w-full min-w-[820px] border-separate border-spacing-0 text-left text-sm'>
          <thead><tr className='text-xs uppercase text-[var(--text-muted)]'>
            <th className='px-4 py-3'>{t('Name')}</th><th className='px-4 py-3 text-right'>{t('Total Purchases')}</th><th className='px-4 py-3 text-right'>{t('Total Payments')}</th><th className='px-4 py-3 text-right'>{t('Balance')}</th><th className='px-4 py-3 text-right'>{t('Actions')}</th>
          </tr></thead>
          <tbody>{items.map((item) => (
            <tr key={item._id} className='hover:bg-[var(--surface-panel-muted)]'>
              <td className='px-4 py-4'>
                {item.role === 'admin' ? <span className='font-semibold'>{name(item)}</span> : <Link className='font-semibold text-[var(--brand)] hover:underline' to={link(item)}>{name(item)}</Link>}
                <div className='mt-1 text-xs text-[var(--text-muted)]'>{item.email}</div>
              </td>
              {item.role === 'admin' ? <td colSpan={3} className='px-4 py-4 text-center text-[var(--text-muted)]'>{t('Administrative account')}</td> : <FinancialValues item={item} showValue={showValue} t={t} />}
              <td className='px-4 py-4'><div className='flex justify-end gap-2'>{isCustomer && <ActionLink label={t('View associated users')} to={`/users?clientId=${item._id}`}><FiUsers /></ActionLink>}{isCustomer && canReconcile && item.role !== 'admin' && <ActionButton label={t('Reconcile')} onClick={() => onReconcile(item)}><FiRefreshCcw /></ActionButton>}<ActionButton label={t('Edit')} onClick={() => onEdit(item)}><FiEdit2 /></ActionButton><ActionButton label={t('Delete')} danger onClick={() => onDelete(item)}><FiTrash2 /></ActionButton></div></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </>
  );
};
